// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { Button, IconButton, List, ListItem, ListItemText, Paper, Switch, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import get from 'lodash/get'
import React from 'react'
import { Mutation, Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { i18n } from '../../index'
import CheckQuestionDialog from './CheckQuestionDialog'
import css from './chainCheckQuestion.css'
import {
  chainCheckQuestionsQuery,
  createStationCheckQuestionMutation,
  deleteStationCheckQuestionMutation,
  setChainCheckQuestionActiveMutation,
  stationCheckQuestionSettingsQuery,
  stationCheckQuestionsQuery,
  updateStationCheckQuestionMutation,
} from './checkQuestionQueries'

const CHECK_TYPES = ['toilet', 'buffet', 'vitrine', 'hall', 'market']

type CheckQuestion = {
  id: string
  type: string
  description: string
}

type Setting = {
  id: string
  active: boolean
  checkQuestion: { id: string }
}

type Props = {
  stationId: string
}

type State = {
  isDialogOpen: boolean
  dialogType: string
  editData: CheckQuestion | null
}

class StationCheckQuestionSection extends React.Component<Props, State> {
  state = {
    isDialogOpen: false,
    dialogType: 'toilet',
    editData: null,
  }

  openCreate = (type: string) => {
    this.setState({ isDialogOpen: true, dialogType: type, editData: null })
  }

  openEdit = (question: CheckQuestion) => {
    this.setState({ isDialogOpen: true, dialogType: question.type, editData: question })
  }

  closeDialog = () => {
    this.setState({ isDialogOpen: false, editData: null })
  }

  groupByType = (questions: CheckQuestion[]) => {
    return questions.reduce(
      (acc, q) => {
        if (!acc[q.type]) {
          acc[q.type] = []
        }
        acc[q.type].push(q)
        return acc
      },
      {} as Record<string, CheckQuestion[]>,
    )
  }

  // A chain question is ON unless a setting row says active === false.
  isChainQuestionActive = (settings: Setting[], checkQuestionId: string) => {
    const setting = settings.find((s) => s.checkQuestion && s.checkQuestion.id === checkQuestionId)
    return setting ? setting.active : true
  }

  render() {
    const { stationId } = this.props
    const { isDialogOpen, dialogType, editData } = this.state
    const refetchVariables = { stationId }
    return (
      <Paper>
        <div className={css.wrapper} id='tarkastuskysymykset'>
          <div className={css.top}>
            <Typography variant='h6'>{i18n.t('admin.checkQuestions.stationPageTitle')}</Typography>
          </div>
          <Query query={chainCheckQuestionsQuery} fetchPolicy='network-only'>
            {({ loading: l1, error: e1, data: chainData }) => (
              <Query query={stationCheckQuestionsQuery} variables={{ stationId }} fetchPolicy='network-only'>
                {({ loading: l2, error: e2, data: stationData }) => (
                  <Query query={stationCheckQuestionSettingsQuery} variables={{ stationId }} fetchPolicy='network-only'>
                    {({ loading: l3, error: e3, data: settingsData }) => {
                      const chainQuestions = get(chainData, 'chainCheckQuestions', [])
                      const stationQuestions = get(stationData, 'stationCheckQuestions', [])
                      const settings = get(settingsData, 'stationCheckQuestionSettings', [])
                      const chainByType = this.groupByType(chainQuestions)
                      const stationByType = this.groupByType(stationQuestions)
                      return (
                        <Loader loading={l1 || l2 || l3} error={e1 || e2 || e3}>
                          {CHECK_TYPES.map((type) => (
                            <div key={type} className={css.group}>
                              <div className={css.groupHeader}>
                                <Typography variant='subtitle1'>{i18n.t(`checkList.checklistType.${type}`)}</Typography>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  color='primary'
                                  onClick={() => this.openCreate(type)}
                                >
                                  {i18n.t('admin.checkQuestions.addQuestion')}
                                </Button>
                              </div>
                              <List dense>
                                {/* Chain questions with per-station on/off toggle */}
                                {(chainByType[type] || []).map((q) => (
                                  <ListItem key={q.id} divider className={css.item}>
                                    <ListItemText primary={q.description} />
                                    <Mutation
                                      mutation={setChainCheckQuestionActiveMutation}
                                      refetchQueries={[
                                        { query: stationCheckQuestionSettingsQuery, variables: { stationId } },
                                      ]}
                                    >
                                      {(onToggle) => (
                                        <Switch
                                          color='primary'
                                          checked={this.isChainQuestionActive(settings, q.id)}
                                          onChange={(e) =>
                                            onToggle({
                                              variables: {
                                                stationId,
                                                checkQuestionId: q.id,
                                                active: e.target.checked,
                                              },
                                            })
                                          }
                                        />
                                      )}
                                    </Mutation>
                                  </ListItem>
                                ))}
                                {/* Station's own questions with edit/delete */}
                                {(stationByType[type] || []).map((q) => (
                                  <ListItem key={q.id} divider className={css.item}>
                                    <ListItemText primary={q.description} />
                                    <IconButton size='small' aria-label='edit' onClick={() => this.openEdit(q)}>
                                      <EditIcon fontSize='small' />
                                    </IconButton>
                                    <Mutation
                                      mutation={deleteStationCheckQuestionMutation}
                                      refetchQueries={[{ query: stationCheckQuestionsQuery, variables: { stationId } }]}
                                    >
                                      {(onDelete) => (
                                        <IconButton
                                          size='small'
                                          aria-label='delete'
                                          onClick={() => onDelete({ variables: { id: q.id } })}
                                        >
                                          <DeleteIcon fontSize='small' />
                                        </IconButton>
                                      )}
                                    </Mutation>
                                  </ListItem>
                                ))}
                              </List>
                            </div>
                          ))}
                        </Loader>
                      )
                    }}
                  </Query>
                )}
              </Query>
            )}
          </Query>
        </div>
        <CheckQuestionDialog
          isOpen={isDialogOpen}
          type={dialogType}
          stationId={stationId}
          data={editData}
          createMutation={createStationCheckQuestionMutation}
          updateMutation={updateStationCheckQuestionMutation}
          refetchQuery={stationCheckQuestionsQuery}
          refetchVariables={refetchVariables}
          onClose={this.closeDialog}
        />
      </Paper>
    )
  }
}

export default StationCheckQuestionSection
